import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import Call from '@icon/Call';
import Typography from 'aquilla/core/atoms/Typography';
import useWindowSize from '@hooks/useWindowSize';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import { useRouter } from 'next/router';
import useCitySpecificiOfflineCentreStore from '@stores/city-specific-offline-centres';
import { useEffect, useState } from 'react';
import { TTU_NUMBER_CLICKED } from '@constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { OFFLINE_VISIT_THE_CENTRE } from './constants';

const Container = styled.div`
  height: ${Spacings.SPACING_18B};
  position: fixed;
  bottom: 0px;
  width: 100%;
  display: flex;
  border-top: ${Spacings.SPACING_1} solid var(--color-divider);
  background-color: var(--color-base-1);
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  z-index: 1000;
`;

const Phone = styled.a`
  display: flex;
  width: fit-content;
`;

const StyP3 = styled(Typography)`
  color: var(--color-text-secondary);
  font-weight: ${Fonts.HEAVY};
  margin-bottom: ${Spacings.SPACING_6};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CallDiv = styled.div`
  display: block;
`;

const StyButton = styled(Button)`
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_38B};
  font-size: ${Fonts.H6};
  border-radius: ${Spacings.SPACING_2B};
`;

const BatchFooter = ({ data }) => {
  const { isMobile } = useWindowSize();
  const { callUsText, cta, ctaDeepLink, phoneNumber, goalName } = data;
  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const {
    centresListData,
    setShiftCookiesBanner,
    isBookAVisitEnabledFromCMS,
    setBookAVisitClicked,
    setBookAVisitFlow
  } = useCitySpecificiOfflineCentreStore();
  const [centreData, setCentreData] = useState({});
  useEffect(() => {
    setCentreData(
      centresListData?.find((centre) => {
        return centre.uid === centreUID;
      })
    );
    if (isMobile) setShiftCookiesBanner(true);
    else setShiftCookiesBanner(false);
  }, [centresListData, centreUID, isMobile, setShiftCookiesBanner]);

  const { centre_name, city, state } = centreData || {};

  const onPhoneNumberClick = () => {
    segment.track(
      TTU_NUMBER_CLICKED,
      {
        inbound_phone_number: phoneNumber,
        batch_group_type: 'Offline'
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
  };

  const handleClick = () => {
    segment.track(OFFLINE_VISIT_THE_CENTRE, {
      centre_name,
      city,
      state,
      goal_name: goalName,
      goal_uid: routeGoalUID,
      block_position: 'Bottom'
    });

    if (!isLoggedIn) {
      openLoginDrawer();
      if (isBookAVisitEnabledFromCMS) setBookAVisitClicked(true);
    } else if (isBookAVisitEnabledFromCMS) {
      setBookAVisitFlow(true);
    } else pushRoute(ctaDeepLink, null, null, null, null, false);
  };

  if (!isMobile) {
    return null;
  }

  return (
    <Container>
      <Wrapper>
        <StyButton
          label={cta}
          onClick={handleClick}
          variant="filled"
          color="black"
          width="152px"
          maxHeight="sm"
        />
        {phoneNumber && (
          <>
            <StyP3 variant="p3">OR</StyP3>
            <CallDiv>
              <StyP3 variant="p3">{callUsText}</StyP3>
              <Phone href={`tel:${phoneNumber}`} onClick={onPhoneNumberClick}>
                <Call
                  size={Spacings.SPACING_5B}
                  viewBox="0 -4 24 24"
                  color="var(--color-text-primary)"
                />
                <Typography variant="h6">{phoneNumber}</Typography>
              </Phone>
            </CallDiv>
          </>
        )}
      </Wrapper>
    </Container>
  );
};

export default BatchFooter;
